import {
  PM11_IN_TIME_TARGET,
  PM11_OLD_BUG_WORKING_DAYS,
  type SupportReport,
  type SupportReportTopic,
  TICKET_STATUSES,
} from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../lib/api';
import { TICKET_TONE } from '../../lib/support';
import { Badge, EmptyState, ErrorBox, Spinner } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { Panel } from '../../ui/layout';

const num = (v: number | null, unit = '') => (v === null ? 'нет данных' : `${v}${unit}`);

function Topics({ title, rows, hint }: { title: string; rows: SupportReportTopic[]; hint?: string }) {
  return (
    <Panel title={title}>
      {hint ? <p className="mb-2 text-[13px] text-ink-2">{hint}</p> : null}
      {rows.length === 0 ? (
        <p className="text-[14px] text-ink-2">Обращений за период нет.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-line">
          {rows.map((r) => (
            <li
              key={r.topic}
              className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-[14px]"
            >
              <span className="min-w-0 [overflow-wrap:anywhere]">{r.topic}</span>
              <span className="num text-[13px] text-ink-2">
                всего {r.total}, человеку {r.byHuman}
                {r.unsolved ? `, не решено ${r.unsolved}` : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/**
 * Критерий приёмки ПМ-11 по журналу обращений: первый ответ в срок БЗ-24 не менее чем в 95 % случаев и ни одной
 * открытой ошибки старше пяти рабочих дней. Старые ошибки — на сегодня, независимо от периода отчёта.
 */
function Pm11({ d }: { d: SupportReport }) {
  const inTimeOk = d.inTimePercent !== null && d.inTimePercent >= PM11_IN_TIME_TARGET;
  const ok = (d.inTimePercent === null || inTimeOk) && d.oldBugs === 0;
  return (
    <Panel title="Приёмка: сроки ответа и старые ошибки">
      <div className="flex flex-col gap-3 text-[14px]">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone={ok ? 'success' : 'danger'}>{ok ? 'Выполняется' : 'Не выполняется'}</Badge>
          <span className="text-[13px] text-ink-2">
            цель: ответ в срок от {PM11_IN_TIME_TARGET} %, открытых ошибок старше {PM11_OLD_BUG_WORKING_DAYS}{' '}
            рабочих дней нет
          </span>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <div className="rounded-lg border border-line bg-surface p-3">
            <div className="text-[12px] text-ink-2">Первый ответ в срок</div>
            <div className={`mt-1 text-[18px] leading-6 font-semibold ${inTimeOk ? '' : 'text-danger'}`}>
              {d.inTimePercent === null ? 'нет данных' : `${String(d.inTimePercent).replace('.', ',')} %`}
            </div>
            <div className="text-[12px] text-ink-2">из {d.inTimeCounted} обращений</div>
          </div>
          <div className="rounded-lg border border-line bg-surface p-3">
            <div className="text-[12px] text-ink-2">Ошибок старше {PM11_OLD_BUG_WORKING_DAYS} раб. дней</div>
            <div className={`mt-1 text-[18px] leading-6 font-semibold ${d.oldBugs ? 'text-danger' : ''}`}>
              {d.oldBugs}
            </div>
          </div>
          <div className="rounded-lg border border-line bg-surface p-3">
            <div className="text-[12px] text-ink-2">Ждут выкладки выпуска</div>
            <div className="mt-1 text-[18px] leading-6 font-semibold">{d.waitingRelease}</div>
          </div>
        </div>
        {d.oldBugList.length ? (
          <ul className="flex flex-col divide-y divide-line">
            {d.oldBugList.map((b) => (
              <li key={b.id}>
                <Link
                  to="/support/tickets/$id"
                  params={{ id: b.id }}
                  className="flex min-h-11 flex-col gap-1 py-2 hover:bg-surface-2"
                >
                  <span className="flex flex-wrap items-center gap-2 text-[12px] text-ink-2">
                    {b.number}
                    <Badge tone={TICKET_TONE[b.status]}>{TICKET_STATUSES[b.status]}</Badge>
                    {b.workingDays} раб. дн.
                  </span>
                  <span className="[overflow-wrap:anywhere]">{b.subject}</span>
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </Panel>
  );
}

/** Отчёт поддержки (БЗ-27): решено без человека, первый ответ, сроки, оценки, частые темы; приёмка ПМ-11 */
export function SupportReportPanel() {
  const [days, setDays] = useState(30);
  const q = useQuery({
    queryKey: ['support', 'report', days],
    queryFn: () => api<SupportReport>('/support/report', { query: { days } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const d = q.data;
  const tiles = [
    { label: 'Обращений', value: String(d.total) },
    { label: 'Решено Pilot без человека', value: num(d.pilotSolvedPercent, '%') },
    { label: 'Первый ответ, в среднем', value: num(d.avgFirstResponseMinutes, ' мин') },
    { label: 'Нарушений срока', value: String(d.slaBreaches) },
    { label: 'Оценено ответов', value: String(d.rated) },
    { label: 'Средняя оценка', value: num(d.avgScore, ' из 5') },
    { label: 'Оценили «решено»', value: num(d.solvedPercent, '%') },
    { label: 'Передано разработке', value: `${d.handedToDev}, выпущено ${d.releasedFixes}` },
  ];
  return (
    <div className="flex flex-col gap-4">
      <div className="max-w-xs">
        <Select
          className="h-11 md:h-10"
          aria-label="Период отчёта"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
        >
          <option value={7}>7 дней</option>
          <option value={30}>30 дней</option>
          <option value={90}>90 дней</option>
          <option value={365}>Год</option>
        </Select>
      </div>
      <Pm11 d={d} />
      {d.total === 0 ? (
        <EmptyState title="За период обращений нет" />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {tiles.map((t) => (
              <div key={t.label} className="rounded-lg border border-line bg-surface p-3">
                <div className="text-[12px] text-ink-2">{t.label}</div>
                <div className="mt-1 text-[18px] leading-6 font-semibold">{t.value}</div>
              </div>
            ))}
          </div>
          <Topics
            title="Список доработок инструкций и интерфейса"
            hint="Разделы, по которым вопрос чаще всего уходит человеку: сюда добавить инструкцию или упростить экран"
            rows={d.improvements}
          />
          <div className="grid gap-4 lg:grid-cols-2">
            <Topics title="Темы по разделам" rows={d.bySection} />
            <Topics title="Темы по ролям авторов" rows={d.byRole} />
          </div>
        </>
      )}
    </div>
  );
}
